import { CSSObject } from '../theming';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { fallbackTheme } from '../theming/fallbackTheme';

type FocusRingConfig = {
  radius: number;
  type: 'pseudo' | 'shadow';
  inset: boolean;
  ringOffset: number;
  theme?: BabbelTheme;
};

const defaultRingOffset = 3;

const defaultConfig: FocusRingConfig = {
  radius: 4,
  type: 'pseudo',
  inset: false,
  ringOffset: defaultRingOffset,
};

const debug =
  process.env.NODE_ENV !== 'production' &&
  typeof window !== 'undefined' &&
  window.localStorage.getItem('debugFocus') === 'true';

/**
 * Utility function that provides styling that is applied on keyboard focus to custom interactive elements (i.e. links, buttons, inputs). Component is used internally by design system components.
 *
 * @param config.radius - default `4`, required for `pseudo`, other will use border of the element. Set to focused element radius value.
 * @param config.type - default `pseudo`
 *
 * `type: pseudo` doesn't work:
 * - for elements which can't have children, like input, select, svg etc.
 * - for elements with `overflow: hidden`
 *
 * We use `shadow` and `pseudo` workarounds instead of outline, because not all browsers support "outline following the shape of border-radius".
 * See https://caniuse.com/mdn-css_properties_outline. Right now almost all browser implented this feature, except:
 *
 * - Safari. See bug [20807](https://bugs.webkit.org/show_bug.cgi?id=20807).
 *
 * [online documentation](https://doc.babbel.com/lessonnine/design-system.lib/index.html?path=/docs/components-utils-focusstyles--page)
 */
export function focusStyles(config?: Partial<FocusRingConfig>): CSSObject {
  const { radius, type, inset, ringOffset, theme } = config
    ? { ...defaultConfig, ...config }
    : defaultConfig;

  if (type === 'shadow') {
    const focus = {
      outline: 'none',
      boxShadow: inset
        ? `inset 0 0 0 2px var(--focus-color, ${fallbackTheme(theme).color.other.focus})`
        : `0 0 0 2px var(--bg-color, ${
            fallbackTheme(theme).color.neutral.surfaceDefault
          }), 0 0 0 4px var(--focus-color, ${fallbackTheme(theme).color.other.focus})`,
    };
    return {
      ...(debug ? focus : {}),
      ':focus': focus,
      ':focus:not(:focus-visible)': {
        boxShadow: 'none',
      },
      '::-moz-focus-inner': {
        border: 0,
        padding: 0,
      },
    };
  }

  const offset = inset ? 0 : ringOffset;

  const focus: CSSObject = {
    outline: 'none',
    ':after': {
      position: 'absolute',
      content: '""',
      borderRadius: radius + offset,
      top: -offset,
      right: -offset,
      bottom: -offset,
      left: -offset,
      boxShadow: inset
        ? `inset 0 0 0 2px var(--focus-color, ${fallbackTheme(theme).color.other.focus})`
        : `0 0 0 2px var(--focus-color, ${fallbackTheme(theme).color.other.focus})`,
    },
  };

  return {
    ...(debug ? focus : {}),
    position: 'relative',
    ':focus': focus,
    // Safari and IE don't support `:focus-visible` https://caniuse.com/css-focus-visible
    // they will ignore this rule
    ':focus:not(:focus-visible)': {
      ':after': {
        display: 'none',
      },
    },
    // Reset Firefox doted line focus ring
    '::-moz-focus-inner': {
      border: 0,
      padding: 0,
    },
  };
}

export function backgroundStyles(
  backgroundColor: string,
  focusColor?: string,
  theme?: BabbelTheme
): CSSObject {
  return {
    backgroundColor,
    '--bg-color': backgroundColor,
    '--focus-color': focusColor
      ? focusColor
      : backgroundColor === fallbackTheme(theme).color.brand.default
      ? fallbackTheme(theme).color.neutral.surfaceDefault
      : fallbackTheme(theme).color.brand.default,
  };
}
